import {formatDate, formatDay, get, pipe, startOfDate} from '@yandex-delivery/utils'

type OfferInfo = {
  /**
   * UNIX или UTC время предлагаемого начала доставки
   * */
  from: number
  /**
   * UNIX или UTC время предлагаемого начала доставки
   * */
  to: number
  /**
   * Будет ли заказ доставляться почтой россии
   * */
  delivered_by_post?: boolean
}

type NddClaimOffersResponse = {
  offers: OfferInfo[]
}

type DayTime = {
  /**
   * Часы
   * */
  hours?: number
  /**
   * Минуты
   * */
  minutes?: number
}
type StationScheduleRestriction = {
  /**
   * Номера дней недели, к которым применяется правило. 1 - понедельник, 2 - вторник, ..., 7 - воскресенье.
   * */
  days?: number[]
  /**
   * Время начала работы.
   * */
  time_from?: DayTime
  /**
   * Время окончания работы.
   * */
  time_to?: DayTime
}
type StationSchedule = {
  /**
   * Часовая зона; смещение в часах относительно всемирного координированного времени UTC.
   * */
  time_zone?: number
  /**
   * Правила, задающие расписание работы.
   * */
  restrictions?: StationScheduleRestriction[]
}

const _formatDay = formatDay('dd')
const isRestrictionValid = (
  restriction: StationScheduleRestriction,
): restriction is Required<StationScheduleRestriction> => {
  return !(
    restriction.days === null ||
    restriction.days === undefined ||
    restriction.time_from === null ||
    restriction.time_from === undefined ||
    restriction.time_to === null ||
    restriction.time_to === undefined
  )
}

type ScheduleItem = {day: number; timeRange: string}

const getScheduleIntervals = (schedule: ScheduleItem[]) => {
  const intervals = []

  let left = 0
  let right = 0

  while (right < schedule.length) {
    if (
      schedule[right]!.day !== (schedule[right + 1]?.day || NaN) - 1 ||
      schedule[right]!.timeRange !== schedule[right + 1]?.timeRange
    ) {
      intervals.push(
        left === right
          ? {days: [schedule[left]!.day], timeRange: schedule[left]!.timeRange}
          : {days: [schedule[left]!.day, schedule[right]!.day], timeRange: schedule[left]!.timeRange},
      )

      left = right + 1
    }
    right++
  }

  return intervals
}

export const formatSchedule = (schedule: StationSchedule, scheduleEveryDayText: string): string[] => {
  const map = (schedule.restrictions || [])
    .filter(isRestrictionValid)
    .reduce<ScheduleItem[]>((acc, {days, time_from, time_to}) => {
      if (
        time_from.hours !== undefined &&
        time_from.minutes !== undefined &&
        time_to.hours !== undefined &&
        time_to.minutes !== undefined
      ) {
        const timeRange: string = `${time_from.hours}:${time_from.minutes.toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        })} - ${time_to.hours}:${time_to.minutes.toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        })}`

        days.forEach(day => acc.push({day, timeRange}))
      }
      return acc
    }, [])
    .sort((a, b) => a.day - b.day)

  const intervals = getScheduleIntervals(map)

  return intervals.map(interval =>
    [
      // Если интервал с понедельника - 1 по воскресенье - 7, то пишем ежедневно
      interval.days[0] === 1 && interval.days[1] === 7
        ? scheduleEveryDayText
        : interval.days.map(day => _formatDay(day).toUpperCase()).join(' - '),
      interval.timeRange,
    ].join(' ・ '),
  )
}

export const formatTime = (list: NddClaimOffersResponse) => {
  const mayBeFrom = get('offers[0].from', list)

  if (mayBeFrom === undefined) {
    return ''
  }

  return pipe(mayBeFrom, startOfDate('day'), formatDate('DD.MM.YYYY'))
}
